// structureComplementaryData.ts
import { safeKeyComponent } from "./common";
import { storyPrefix, VideoPrefix } from "./path";

/** ---------- Types ---------- */

export type MediaKind = "image" | "video" | "audio" | "unknown";

export interface ExistingMediaItem {
/** CDN or key path, e.g. "https://cdn.../stories/..." or "stories/..." */
url?: string;
/** Optional preview (ignored for classification) */
preview?: string;
/** If already has a key-like "stories/..."/"videos/..." keep it */
name?: string;
/** Optional MIME */
type?: string;
/** Optional byte size (for new uploads we may have File.size) */
size?: number;
isFeatured?: boolean;
}

export interface NewMediaItem {
/** New upload */
file: File;
isFeatured?: boolean;
}

export type AnyMediaItem = ExistingMediaItem | NewMediaItem;

export interface StructureInput {
complementaryMedia?: AnyMediaItem[];
mediaFiles?: AnyMediaItem[];
complementaryText?: any[];
richText?: any[];
storyTitle?: string;
}

export interface ComplementaryEntryBase {
fileName: string;
isFeatured?: boolean;
}

export interface ComplementaryVideoEntry extends ComplementaryEntryBase {
/** "pending" until transcoder marks ready */
status?: "pending" | "done" | "ready";
/** Optional fields added later (not set here) */
thumb?: string;
versions?: Array<{ video: string; resolution: string }>;
}

export interface MemoryDetailsOut {
complementaryImage?: ComplementaryEntryBase[];
complementaryVideo?: ComplementaryVideoEntry[];
complementaryAudio?: ComplementaryEntryBase[];
complementaryText?: any[];
richText?: any[];
}

export type MemoryType = "text" | "image" | "video" | "audio";

export interface StructureOutput {
memory_details: MemoryDetailsOut;
totalSize: number;
type: MemoryType;
}

/** ---------- Helpers ---------- */

export const VIDEO_EXT = new Set(["mp4", "mov", "m4v", "webm", "mkv", "avi"]);
export const IMAGE_EXT = new Set(["webp", "jpg", "jpeg", "png", "heic", "heif", "gif", "bmp"]);
export const AUDIO_EXT = new Set(["mp3", "aac", "wav", "m4a", "ogg", "flac", "aiff", "alac", "amr", "wma", "opus", "3gp"]);


export const removeCrossCategoryDuplicates = (md: any) => {
// If the same fileName appears in audio & video, keep only one.
// For .webm default to audio unless under videos/...
const audioNames = new Set<string>((md.complementaryAudio || []).map((a: any) => a.fileName));
md.complementaryVideo = (md.complementaryVideo || []).filter((v: any) => {
  if (!audioNames.has(v.fileName)) return true;
  const ext = getExt(v.fileName);
  const vidByPath = v.fileName.startsWith('videos/');
  const ambiguous = ext === 'webm';
  // Keep video entry only when path says videos/ and it's not ambiguous favoring audio
  return vidByPath && !ambiguous;
});

// Also ensure unique within each category
md.complementaryAudio = uniqueByFileName(md.complementaryAudio || []);
md.complementaryVideo = uniqueByFileName(md.complementaryVideo || []);
md.complementaryImage = uniqueByFileName(md.complementaryImage || []);
};

export const classifyKind = (file: any): 'image' | 'video' | 'audio' => {
const mime = (file?.type || '').toLowerCase();
const nameOrUrl = file?.url || file?.name || '';
const ext = getExt(nameOrUrl);

// 1) MIME has highest priority
if (mime.startsWith('audio/')) return 'audio';
if (mime.startsWith('video/')) return 'video';
if (mime.startsWith('image/')) return 'image';

// 2) Extension
if (AUDIO_EXT.has(ext) && VIDEO_EXT.has(ext)) {
  // Ambiguous (e.g., webm): prefer AUDIO unless path clearly indicates video
  if (nameOrUrl.startsWith('videos/')) return 'video';
  return 'audio';
}
if (AUDIO_EXT.has(ext)) return 'audio';
if (VIDEO_EXT.has(ext)) return 'video';
if (IMAGE_EXT.has(ext)) return 'image';

// 3) Prefix fallback (rare)
if (nameOrUrl.startsWith('videos/')) return 'video';
return 'image'; // safest default so thumbnails don't become videos
};

const getExt = (name = ""): string => (name.split(".").pop() || "").toLowerCase();

const isMime = (f: unknown, kind: Exclude<MediaKind, "unknown">): boolean =>
typeof (f as { type?: string })?.type === "string" &&
(f as { type: string }).type.toLowerCase().startsWith(`${kind}/`);

const kindFromExt = (ext: string): MediaKind => {
if (VIDEO_EXT.has(ext)) return "video";
if (AUDIO_EXT.has(ext)) return "audio";
if (IMAGE_EXT.has(ext)) return "image";
return "unknown";
};

const kindFromPrefix = (name: string): MediaKind => {
if (name.startsWith("videos/")) return "video";
if (name.startsWith("stories/")) return "image";
return "unknown";
};

const uniqueByFileName = <T extends { fileName?: string }>(arr: T[] = []): T[] => {
const seen = new Set<string>();
return (arr || []).filter((x) => {
  const k = x?.fileName;
  if (!k || seen.has(k)) return false;
  seen.add(k);
  return true;
});
};

const stripCdn = (u = ""): string =>
// https://cdn.memvy.com/stories/... -> stories/...
u.replace(/^https?:\/\/[^/]+\/+/i, "");

/** Normalize incoming union into a consistent shape we can reason about */
function pickFileShape(item: AnyMediaItem): { src: File | ExistingMediaItem; isFeatured: boolean } | null {
if ((item as NewMediaItem)?.file instanceof File) {
  return { src: (item as NewMediaItem).file, isFeatured: !!item.isFeatured };
}
if ((item as ExistingMediaItem)?.url || (item as ExistingMediaItem)?.name) {
  return { src: item as ExistingMediaItem, isFeatured: !!item.isFeatured };
}
return null;
}

function isAlreadyUploaded(src: File | ExistingMediaItem): boolean {
const n = (src as ExistingMediaItem)?.name || (src as ExistingMediaItem)?.url || "";
return Boolean((src as ExistingMediaItem)?.url) || n.includes("stories/") || n.includes("videos/");
}

/** ---------- Main ---------- */

export const structureComplementaryData = async (data: StructureInput): Promise<StructureOutput> => {
const { complementaryMedia, complementaryText, mediaFiles, richText, storyTitle } = data;


const newData: StructureOutput = {
  memory_details: {},
  totalSize: 0,
  type: "text",
};

const allMedia: AnyMediaItem[] = [...(complementaryMedia || []), ...(mediaFiles || [])];

// We will collect BOTH:
// - carryOver: unchanged media (already on CDN) → keep in payload
// - toProcess: new uploads only
const carryOver: Record<Exclude<MediaKind, "unknown">, ComplementaryEntryBase[]> = {
  image: [],
  video: [],
  audio: [],
};
const toProcess: Array<{
  raw: AnyMediaItem;
  src: File | ExistingMediaItem;
  isFeatured: boolean;
  kind: Exclude<MediaKind, "unknown">;
}> = [];

const safeTitle = safeKeyComponent(storyTitle || "untitled");

for (let i = 0; i < allMedia.length; i++) {
  const raw = allMedia[i];
  const picked = pickFileShape(raw);
  if (!picked) continue;

  const { src, isFeatured } = picked;

  const nameOrUrl = (src as ExistingMediaItem)?.url || (src as ExistingMediaItem)?.name || (src as File)?.name || "";
  const ext0 = getExt(nameOrUrl);

  // Determine kind (MIME > ext > prefix)
  let kind: MediaKind =
    isMime(src, "video")
      ? "video"
      : isMime(src, "audio")
        ? "audio"
        : isMime(src, "image")
          ? "image"
          : kindFromExt(ext0);

  if (kind === "unknown") {
    const k2 = kindFromPrefix(nameOrUrl);
    if (k2 !== "unknown") kind = k2;
  }
  if (kind === "unknown") kind = "image"; // safe default

  if (isAlreadyUploaded(src)) {
    // ✅ carry over unchanged media
    const fileName = stripCdn(nameOrUrl);
    const entry: ComplementaryEntryBase = { fileName, isFeatured: !!isFeatured };

    if (kind === "video") carryOver.video.push(entry);
    else if (kind === "audio") carryOver.audio.push(entry);
    else carryOver.image.push(entry);
  } else {
    // New upload → process later
    toProcess.push({
      raw,
      src,
      isFeatured: !!isFeatured,
      kind: kind as Exclude<MediaKind, "unknown">,
    });
  }
}

// Map NEW uploads only → create canonical fileName in correct folder
const mappedNew = toProcess.map((item, idx) => {
  const { src, isFeatured, kind } = item;
  const baseName =
    ((src as ExistingMediaItem)?.name || (src as ExistingMediaItem)?.url || (src as File)?.name || `file_${idx}`)
      .split("/")
      .pop() || `file_${idx}`;

  const dir =
    kind === "video"
      ? `${VideoPrefix}/${safeTitle}/memories/`
      : `${storyPrefix}/${safeTitle}/memories/`;

  const preNamed =
    typeof (src as ExistingMediaItem)?.name === "string" &&
    (((src as ExistingMediaItem).name as string).startsWith("videos/") ||
      (src as ExistingMediaItem).name!.startsWith("stories/"));

  const fileName = preNamed ? (src as ExistingMediaItem).name! : `${dir}${baseName}`;

  // size accounting if File
  const size = (src as File)?.size ?? (src as ExistingMediaItem)?.size ?? 0;
  if (size) newData.totalSize += size;

  const status: ComplementaryVideoEntry["status"] = kind === "video" ? "pending" : "done";

  return { fileName, isFeatured: !!isFeatured, status, kind };
});

// Split new uploads by kind
const newImages = mappedNew
  .filter((m) => m.kind === "image")
  .map(({ kind: _k, ...rest }) => rest as ComplementaryEntryBase);

const newVideos = mappedNew
  .filter((m) => m.kind === "video")
  .map(({ kind: _k, ...rest }) => rest as ComplementaryVideoEntry);

const newAudios = mappedNew
  .filter((m) => m.kind === "audio")
  .map(({ kind: _k, ...rest }) => rest as ComplementaryEntryBase);

// ✅ Final arrays = carryOver (unchanged) + new uploads
const complementaryImage = uniqueByFileName<ComplementaryEntryBase>([...carryOver.image, ...newImages]);
const complementaryVideo = uniqueByFileName<ComplementaryVideoEntry>([...carryOver.video, ...newVideos]);
const complementaryAudio = uniqueByFileName<ComplementaryEntryBase>([...carryOver.audio, ...newAudios]);

newData.memory_details.complementaryImage = complementaryImage;
newData.memory_details.complementaryVideo = complementaryVideo;
newData.memory_details.complementaryAudio = complementaryAudio;

// Text (don’t nuke if empty)
if (Array.isArray(complementaryText) && complementaryText.length > 0) {
  newData.memory_details.complementaryText = complementaryText;
}
if (Array.isArray(richText) && richText.length > 0) {
  newData.memory_details.richText = richText;
}

// Derive memory type from final arrays
let type: MemoryType = "text";
if (complementaryImage.some((i) => i.isFeatured)) type = "image";
else if (complementaryVideo.length > 0) type = "video";
else if (complementaryAudio.length > 0) type = "audio";

newData.type = type;

return newData;
};
